/**
 * Multi-seleção e o produto cartesiano — o coração do cadastro de conteúdo.
 *
 * No sistema antigo, escolher 3 veículos e 2 sistemas cria **6 conteúdos**, um
 * por combinação. É o que economiza o dia de quem cadastra, e é a razão de o
 * cadastro aceitar listas em vez de um valor por campo.
 *
 * A conta cresce rápido: 5 veículos × 4 motores × 2 assuntos já são 40
 * registros. Por isso o total aparece antes de gravar, e não depois.
 */

import { useMemo, useState } from "react"
import type { OpcaoDeCatalogo } from "@/api/catalogosDeConteudo"

/** Uma combinação. `0` quer dizer "não classificado neste campo", como no banco. */
export type Combinacao = {
  veiculo: number
  motor: number
  assunto: number
  sistema: number
  nome: number
  fabricante: number
}

/**
 * O produto cartesiano dos seis campos.
 *
 * Campo vazio conta como `[0]` — um único valor "sem classificação" — e não como
 * lista vazia. Se contasse como vazia, o produto seria zero e não sairia
 * combinação nenhuma: quem classifica só por veículo não conseguiria gravar.
 */
export function combinacoes(escolhas: {
  veiculos: number[]; motores: number[]; assuntos: number[]
  sistemas: number[]; nomes: number[]; fabricantes: number[]
}): Combinacao[] {
  const ou0 = (xs: number[]) => (xs.length > 0 ? xs : [0])

  const veiculos = ou0(escolhas.veiculos)
  const motores = ou0(escolhas.motores)
  const assuntos = ou0(escolhas.assuntos)
  const sistemas = ou0(escolhas.sistemas)
  const nomes = ou0(escolhas.nomes)
  const fabricantes = ou0(escolhas.fabricantes)

  // Nada escolhido em campo nenhum não é uma combinação: é um formulário vazio.
  const total = veiculos.length * motores.length * assuntos.length
    * sistemas.length * nomes.length * fabricantes.length
  if (total === 1 && veiculos[0] === 0 && motores[0] === 0 && assuntos[0] === 0
      && sistemas[0] === 0 && nomes[0] === 0 && fabricantes[0] === 0) {
    return []
  }

  const saida: Combinacao[] = []
  for (const veiculo of veiculos)
    for (const motor of motores)
      for (const assunto of assuntos)
        for (const sistema of sistemas)
          for (const nome of nomes)
            for (const fabricante of fabricantes)
              saida.push({ veiculo, motor, assunto, sistema, nome, fabricante })

  return saida
}

/** O teto do cadastro em lote da API. */
export const LIMITE_DO_LOTE = 500

export function MultiSelecao({ rotulo, itens, escolhidos, aoMudar }: {
  rotulo: string
  itens: OpcaoDeCatalogo[]
  escolhidos: number[]
  aoMudar: (ids: number[]) => void
}) {
  const [busca, setBusca] = useState("")
  const [aberto, setAberto] = useState(false)

  const porId = useMemo(() => new Map(itens.map((x) => [x.id, x.rotulo])), [itens])

  const achados = useMemo(() => {
    const t = busca.trim().toLowerCase()
    const base = t ? itens.filter((x) => x.rotulo.toLowerCase().includes(t)) : itens
    return base.slice(0, 200)
  }, [itens, busca])

  function alternar(id: number) {
    aoMudar(escolhidos.includes(id)
      ? escolhidos.filter((x) => x !== id)
      : [...escolhidos, id])
  }

  return (
    <div style={{ marginBottom: 9 }}>
      <label style={{
        display: "flex", alignItems: "baseline", gap: 6,
        fontSize: 10.5, color: "var(--txt2)", marginBottom: 3,
      }}>
        {rotulo}
        {escolhidos.length > 0 && (
          <span style={{ color: "var(--acao)" }}>{escolhidos.length} escolhido{escolhidos.length === 1 ? "" : "s"}</span>
        )}
      </label>

      {escolhidos.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
          {escolhidos.map((id) => (
            <span key={id} style={{
              display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5,
              padding: "1px 3px 1px 7px", borderRadius: 3, background: "var(--surface2)",
              maxWidth: 240,
            }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {porId.get(id) ?? `#${id}`}
              </span>
              <button
                onClick={() => alternar(id)}
                aria-label={`Tirar ${porId.get(id) ?? id}`}
                style={{
                  border: 0, background: "transparent", cursor: "pointer",
                  color: "var(--txt3)", fontSize: 12, lineHeight: 1, padding: "0 2px",
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <button
        onClick={() => setAberto((a) => !a)}
        style={{
          width: "100%", textAlign: "left", padding: "5px 7px", fontSize: 11.5,
          border: "1px solid var(--line)", borderRadius: 5, cursor: "pointer",
          background: "var(--surface)", color: "var(--txt2)",
        }}
      >
        {aberto ? "fechar a lista" : escolhidos.length ? "escolher mais…" : "escolher…"}
      </button>

      {aberto && (
        <div style={{
          marginTop: 4, border: "1px solid var(--line2)", borderRadius: 5, padding: 5,
        }}>
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="filtrar…"
            autoFocus
            style={{
              width: "100%", padding: "4px 6px", fontSize: 11, marginBottom: 4,
              border: "1px solid var(--line)", borderRadius: 4,
              background: "var(--surface)", color: "var(--txt)",
            }}
          />
          <div style={{ maxHeight: 170, overflowY: "auto" }}>
            {achados.map((x) => (
              <label key={x.id} style={{
                display: "flex", alignItems: "center", gap: 5, padding: "2px 3px",
                fontSize: 11.5, cursor: "pointer",
              }}>
                <input type="checkbox" checked={escolhidos.includes(x.id)} onChange={() => alternar(x.id)} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {x.rotulo}
                </span>
              </label>
            ))}
            {achados.length === 0 && (
              <p style={{ fontSize: 11, color: "var(--txt3)", padding: 3 }}>Nada com esse texto.</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
